import {
  Column,
  CreateDateColumn,
  Entity,
  Index,
  ManyToMany,
  ManyToOne,
  PrimaryGeneratedColumn,
  RelationId,
  DeleteDateColumn,
  UpdateDateColumn
} from 'typeorm'
import { Field, ID, ObjectType } from 'type-graphql'

import { Domain } from '@things-factory/shell'
import { Group } from '../group/group.js'
import { PlayGroup } from '../play-group/play-group.js'
import { User } from '@things-factory/auth-base'
import { config } from '@things-factory/env'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

@Entity()
@Index('ix_board_1', (board: Board) => [board.domain, board.name], {
  unique: true,
  where: '"deleted_at" IS NULL'
})
@Index('ix_board_3', (board: Board) => [board.domain, board.group])
@ObjectType({ description: 'Represents a visual dashboard or display board.' })
export class Board {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { nullable: true, description: 'Unique identifier for the board.' })
  readonly id?: string

  @Column({ nullable: true, default: 1 })
  @Field({ nullable: true, description: 'The version of the board model.' })
  version?: number = 1

  @ManyToOne(type => Domain)
  @Field(type => Domain, { nullable: true, description: 'The domain to which this board belongs.' })
  domain?: Domain

  @RelationId((board: Board) => board.domain)
  domainId?: string

  @Column()
  @Field({ description: 'The name of the board.' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'A detailed description of the board.' })
  description?: string

  @Column({ nullable: true, default: 'main' })
  @Field({ nullable: true, description: "The type of the board, can be 'main', 'sub', or 'popup'." })
  type?: 'main' | 'sub' | 'popup'

  @Column({ nullable: true, default: 'draft' })
  @Field({ nullable: true, description: "The state of the board, can be 'draft' or 'released'." })
  state?: 'draft' | 'released'

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'The JSON model defining the layout and components of the board.' })
  model?: string

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'A base64 encoded thumbnail image of the board.' })
  thumbnail?: string

  /**
   * Thumbnail 이 *thumbnail 로서* 마지막으로 갱신된 시각. board.updatedAt 과는 별개
   * 도메인 — 메타데이터(name/description) 변경 시에는 갱신되지 않는다.
   *
   * 용도:
   *   - 클라이언트 image cache-bust: `/thumbnail/{id}?v={thumbnailUpdatedAt}`
   *   - subscription/pubsub push 알림 payload
   *   - 운영/분석: stale thumbnail 감지, 자동 재생성 정책
   *
   * Legacy 데이터는 NULL (마이그레이션은 컬럼만 추가, 기존 행은 채우지 않음).
   * 새 thumbnail 이 만들어지는 시점부터 점진적으로 채워진다.
   */
  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Timestamp of the last thumbnail generation. Separate lifecycle from updatedAt; only changes when the thumbnail image itself is regenerated.' })
  thumbnailUpdatedAt?: Date

  @Column({ type: 'float', nullable: true, default: 0 })
  @Field({ nullable: true, description: 'Sort order for display. Lower values appear first. Supports fractional values for insertion.' })
  sortOrder?: number

  /**
   * 이 보드를 zero-to-twin import 로 만든 경우 source ImportSession.id 참조.
   * 일반 사용자 작성 보드는 null. board-import 패키지의 ImportSession entity 와 약결합 —
   * 모듈 의존을 강제하지 않기 위해 ManyToOne 대신 단순 string id 만 보관.
   */
  @Column({ nullable: true })
  @Index('ix_board_4', { unique: false })
  @Field({ nullable: true, description: 'Source ImportSession id (board-import) when this board was generated from a drawing/image import.' })
  sourceImportSessionId?: string

  @ManyToOne(type => Group, group => group.boards)
  @Field(type => Group, { nullable: true, description: 'The group to which this board belongs.' })
  group?: Group

  @RelationId((board: Board) => board.group)
  groupId?: string

  @ManyToMany(type => PlayGroup, playGroup => playGroup.boards)
  @Field(type => [PlayGroup], { nullable: true, description: 'A list of play groups that this board is a part of.' })
  playGroups?: PlayGroup[]

  @CreateDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the board was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the board was last updated.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who created the board.' })
  creator?: User

  @RelationId((board: Board) => board.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who last updated the board.' })
  updater?: User

  @RelationId((board: Board) => board.updater)
  updaterId?: string

  @DeleteDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the board was soft-deleted.' })
  deletedAt?: Date
}
